import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Upload, Music, Palette, Trash2, AlertTriangle, Loader2, Image as ImageIcon,
} from "lucide-react";

export default function MyAssets() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("art"); // "art" | "music"
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [consent, setConsent] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    const kind = tab === "art" ? "user_art" : "user_music";
    api.get("/me/assets", { params: { kind } }).then(r => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [tab, user]);

  if (loading) return <div className="max-w-4xl mx-auto p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/my/assets" replace />;

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file first"); return; }
    if (!consent) { toast.error("You must confirm this is your own original work"); return; }
    const kind = tab === "art" ? "user_art" : "user_music";
    const form = new FormData();
    form.append("kind", kind);
    form.append("consent", "true");
    if (title) form.append("title", title);
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/upload/asset", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setTitle("");
      setConsent(false);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const del = async (fid) => {
    if (!window.confirm("Delete this upload?")) return;
    await api.delete(`/me/assets/${fid}`);
    toast.success("Deleted");
    load();
  };

  const accept = tab === "art" ? "image/jpeg,image/png,image/webp,image/gif" : "audio/mpeg,audio/mp3,audio/wav,audio/mp4,audio/m4a,audio/aac,audio/ogg";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-400 mb-2">My uploads</div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold">Your art &amp; music</h1>
        <p className="text-slate-400 text-sm mt-1">Attach original art or music to your stories.</p>
      </div>

      {/* Warning banner — mandatory */}
      <div data-testid="assets-warning" className="rounded-2xl border border-rose-500/40 bg-rose-500/10 text-rose-100 px-5 py-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-rose-300" />
        <div className="text-sm">
          <div className="font-semibold text-rose-200">Only upload work that is yours.</div>
          <div className="text-rose-200/80">
            Art and music must be your own original creation. If you upload someone else's work it will be removed
            by moderation and your account may be locked. By checking the consent box below you confirm you own the
            rights to what you're uploading.
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        <button
          data-testid="assets-tab-art"
          onClick={() => setTab("art")}
          className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${tab === "art" ? "bg-amber-500 text-black" : "border border-white/10 text-slate-400 hover:text-amber-300"}`}
        >
          <Palette className="h-3.5 w-3.5" /> Art
        </button>
        <button
          data-testid="assets-tab-music"
          onClick={() => setTab("music")}
          className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${tab === "music" ? "bg-amber-500 text-black" : "border border-white/10 text-slate-400 hover:text-amber-300"}`}
        >
          <Music className="h-3.5 w-3.5" /> Music
        </button>
      </div>

      <form onSubmit={upload} className="rounded-2xl border border-white/10 bg-[#0F1117] p-5 mb-8">
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">Title (optional)</Label>
            <Input data-testid="assets-title" value={title} onChange={e => setTitle(e.target.value)}
                   placeholder={tab === "art" ? "e.g. Opera House, oil, 2026" : "e.g. Night Score (feat. my violin)"}
                   className="mt-1 bg-[#07080A] border-white/10 h-11" />
          </div>
          <input ref={fileRef} data-testid="assets-file" type="file" accept={accept}
                 className="text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-amber-500 file:text-black file:font-semibold file:px-4 file:py-2" />
        </div>
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
          <input data-testid="assets-consent" type="checkbox" checked={consent}
                 onChange={e => setConsent(e.target.checked)}
                 className="mt-1 accent-amber-500" />
          <span>
            I confirm this {tab === "art" ? "art" : "music"} is <b>my own original work</b> and I have the rights to upload it.
            I understand that uploading someone else's work will have it removed and may lock my account.
          </span>
        </label>
        <Button data-testid="assets-upload-btn" type="submit" disabled={uploading || !consent}
                className="mt-4 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Upload</>}
        </Button>
      </form>

      <div className="mb-4 flex items-center gap-2 text-sm text-slate-400">
        {items.length === 0 ? "Nothing uploaded here yet." : `${items.length} item${items.length === 1 ? "" : "s"}`}
      </div>
      <div data-testid="assets-grid" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(it => (
          <div key={it.file_id} className="rounded-2xl border border-white/10 bg-[#0F1117] p-4">
            {it.kind === "user_art" ? (
              <img src={fileUrl(it.url)} alt={it.title || it.file_id}
                   className="w-full aspect-[4/3] object-cover rounded-lg border border-white/10 mb-3" />
            ) : (
              <audio controls src={fileUrl(it.url)} className="w-full mb-3" />
            )}
            <div className="font-serif text-base text-white truncate">{it.title || "(untitled)"}</div>
            <div className="text-[10px] font-mono text-slate-500">{new Date(it.created_at).toLocaleDateString()} · {(it.size/1024).toFixed(0)} KB</div>
            <button data-testid={`asset-delete-${it.file_id}`} onClick={() => del(it.file_id)}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200">
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link to="/create" className="inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200 font-mono uppercase tracking-widest">
          <ImageIcon className="h-4 w-4" /> Attach these to a new story
        </Link>
      </div>
    </div>
  );
}
